import { useMemo } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';

import { i18n } from '../../../i18n/index.js';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { useWSChannel } from '../../../shared/ws-client/useWSChannel.js';
import {
  getDealDetailQueryKey,
  getDealsQueryKey,
  listDealsPaginated,
  type DealStageChangedEvent,
  type DealListItem,
  type ListDealsFilters,
} from '../api/deals.client.js';
import { buildKanbanColumns } from '../lib/kanban.js';

export interface UseDealsResult {
  deals: DealListItem[];
  columns: ReturnType<typeof buildKanbanColumns>;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

export function useDeals(filters: ListDealsFilters = {}): UseDealsResult {
  const scope = useCurrentScope();
  const queryClient = useQueryClient();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const query = useQuery({
    queryKey: getDealsQueryKey(workspaceId, filters),
    queryFn: async () => {
      const limit = filters.limit ?? 200;
      let cursor: string | undefined;
      const all: DealListItem[] = [];
      const MAX_PAGES = 5;

      for (let page = 0; page < MAX_PAGES; page++) {
        const result = await listDealsPaginated({
          ...filters,
          limit,
          ...(cursor === undefined ? {} : { cursor }),
        });
        all.push(...result.data);

        if (!result.hasMore) break;

        cursor = result.nextCursor ?? undefined;

        if (page === MAX_PAGES - 1) {
          console.warn(
            `[useDeals] Reached ${MAX_PAGES}-page cap for workspace ${workspaceId}` +
              ` (loaded ${all.length} deals). Increase limit or add scroll-based pagination.`,
          );
        }
      }

      return all;
    },
    enabled: scope !== null,
    retry: false,
    // Фильтры считает сервер, то есть смена фильтра — это новый ключ. Без
    // этого страница на каждую букву поиска сбрасывалась бы в общий спиннер.
    placeholderData: keepPreviousData,
  });

  useWSChannel<DealStageChangedEvent>('/ws/deals', {
    enabled: scope !== null,
    onMessage: (event) => {
      if (event.type !== 'deal.stage.changed' || event.workspaceId !== workspaceId) {
        return;
      }

      // Префикс, а не полный ключ: иначе обновлялся бы только нефильтрованный список.
      void queryClient.invalidateQueries({ queryKey: ['deals', workspaceId] });
      void queryClient.invalidateQueries({
        queryKey: getDealDetailQueryKey(workspaceId, event.dealId),
      });
    },
  });

  const deals = query.data ?? [];
  // buildKanbanColumns formats money and fallback labels via i18n — recompute
  // when the locale changes, not only when the data refetches.
  const locale = i18n.locale;
  const columns = useMemo(() => buildKanbanColumns(deals), [deals, locale]);

  return {
    deals,
    columns,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error instanceof Error ? query.error : null,
    refetch: query.refetch,
  };
}
